import { useState } from 'react';
import { seatLabel } from '@bre-ticketing/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import type { ApiError, CartView, OrderDetail, OrderPaymentSummary, Tender } from '../api';
import { dollars, endpoints, when, writeExchangeContext } from '../api';
import { useManagerPin, usePermissions } from '../session';
import { TenderPanel } from './Sell';
import { Button, ErrorNote } from '../ui';
import { dateWords } from '../terms';

/**
 * Orders at the window: find one by number, name, phone, email or ticket
 * code; will-call pickup with a printed ticket; reprints; a refund back to
 * the way they paid; email or receipt on request.
 */
export default function OrdersPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const results = useQuery({ queryKey: ['orders', submitted], queryFn: () => endpoints.orders(submitted), enabled: submitted.trim().length >= 2 });
  if (id) return <OrderView id={id} onBack={() => void navigate('/orders')} />;
  return (
    <div className="mx-auto max-w-3xl space-y-4">
    <WillCallList onOpen={(orderId) => void navigate(`/orders/${orderId}`)} />
    <section className="rounded-xl border border-line bg-surface">
      <form
        className="flex gap-2 border-b border-line px-5 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(q);
        }}
      >
        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Order number, name, group, phone, email or ticket code" className="touch w-full rounded-lg border border-line px-3" aria-label="Find an order" />
        <Button type="submit" variant="primary" className="touch">
          Find
        </Button>
      </form>
      <ul className="divide-y divide-line">
        {(results.data?.orders ?? []).map((o) => (
          <li key={o.id}>
            <button type="button" onClick={() => void navigate(`/orders/${o.id}`)} className="touch flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-page">
              <span>
                <span className="font-mono font-medium">{o.order_number}</span>
                <span className="ml-2">{o.customer_name_snapshot ?? 'Guest'}</span>
                {o.group_name ? <span className="ml-2 rounded bg-page px-1.5 text-xs font-semibold">Group: {o.group_name}{o.group_booking ? ` · ${o.group_booking}` : ''}</span> : null}
                <span className="block text-xs text-ink-600">
                  {o.first_performance_at ? when(o.first_performance_at) : ''} · {o.live_tickets} live ticket{o.live_tickets === 1 ? '' : 's'} · {o.channel.toLowerCase().replaceAll('_', ' ')}
                  {o.delivery_method === 'WILL_CALL' ? (o.pickups > 0 ? ' · picked up' : ' · WILL CALL') : ''}
                </span>
              </span>
              <span className="tabular">{dollars(o.cached_total_due_cents)}</span>
            </button>
          </li>
        ))}
        {results.data && results.data.orders.length === 0 ? <li className="px-5 py-6 text-sm text-ink-400">Nothing found.</li> : null}
      </ul>
    </section>
    </div>
  );
}

/**
 * Will call for a performance (D-58): every order held at the window, with
 * who picked up and what was printed; Print all queues a cover ticket per
 * order and its tickets on this register's printer, ahead of the rush.
 */
function WillCallList({ onOpen }: { onOpen: (orderId: string) => void }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [performanceId, setPerformanceId] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const performances = useQuery({ queryKey: ['performances'], queryFn: () => endpoints.performances() });
  const list = useQuery({ queryKey: ['will-call', performanceId], queryFn: () => endpoints.willCall(performanceId), enabled: Boolean(performanceId) });
  const print = useMutation({
    mutationFn: (orderIds: string[] | null) => endpoints.printWillCall(performanceId, orderIds ? { order_ids: orderIds } : {}),
    onSuccess: (result) => {
      setError(null);
      setNotice(`Printing ${result.orders} order${result.orders === 1 ? '' : 's'}: ${result.header_job_ids.length} cover ticket${result.header_job_ids.length === 1 ? '' : 's'} and ${result.ticket_job_ids.length} ticket${result.ticket_job_ids.length === 1 ? '' : 's'}.`);
      void queryClient.invalidateQueries({ queryKey: ['will-call'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  if (!can('box_office.will_call')) return null;
  const orders = list.data?.orders ?? [];
  const waiting = orders.filter((o) => o.pickups === 0);
  const unprinted = waiting.filter((o) => !o.fulfillment_printed_at);
  return (
    <section className="rounded-xl border border-line bg-surface">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-5 py-3">
        <span className="font-semibold">Will call</span>
        <select value={performanceId} onChange={(e) => setPerformanceId(e.target.value)} className="touch rounded-lg border border-line px-3" aria-label={`${dateWords().One}`}>
          <option value="">Choose {dateWords().a}</option>
          {(performances.data?.performances ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.event_title} · {when(p.starts_at)}
            </option>
          ))}
        </select>
        {performanceId ? (
          <span className="text-sm text-ink-600">
            {waiting.length} waiting · {orders.length - waiting.length} picked up
          </span>
        ) : null}
        <Button variant="primary" className="touch ml-auto" disabled={!performanceId || print.isPending || unprinted.length === 0} onClick={() => print.mutate(null)}>
          {print.isPending ? 'Printing…' : `Print all waiting (${unprinted.length})`}
        </Button>
      </div>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="px-5 py-2 text-sm text-ink-600">{notice}</p> : null}
      {performanceId ? (
        <ul className="divide-y divide-line">
          {orders.map((o) => (
            <li key={o.id} className="flex items-center gap-3 px-5 py-3">
              <button type="button" onClick={() => onOpen(o.id)} className="touch flex-1 text-left hover:bg-page">
                <span className="font-medium">{o.customer_name ?? 'Guest'}</span>
                <span className="ml-2 font-mono text-sm">{o.order_number}</span>
                <span className="block text-xs text-ink-600">
                  {o.tickets.length} ticket{o.tickets.length === 1 ? '' : 's'} · {o.tickets.map((t) => t.seat_label).join(', ')}
                  {o.customer_phone ? ` · ${o.customer_phone}` : ''}
                </span>
              </button>
              <span className="text-xs whitespace-nowrap text-ink-600">{o.pickups > 0 ? 'PICKED UP' : o.fulfillment_printed_at ? 'printed' : 'not printed'}</span>
              {o.pickups === 0 ? (
                <Button className="touch" disabled={print.isPending} onClick={() => print.mutate([o.id])}>
                  {o.fulfillment_printed_at ? 'Reprint' : 'Print'}
                </Button>
              ) : null}
            </li>
          ))}
          {list.data && orders.length === 0 ? <li className="px-5 py-6 text-sm text-ink-400">No will-call orders for this {dateWords().one}.</li> : null}
        </ul>
      ) : null}
    </section>
  );
}

function OrderView({ id, onBack }: { id: string; onBack: () => void }) {
  const can = usePermissions();
  const askPin = useManagerPin();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useQuery<OrderDetail, ApiError>({ queryKey: ['order', id], queryFn: () => endpoints.order(id) });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['order', id] });
  const pickup = useMutation({
    mutationFn: (method: string) => endpoints.pickup(id, { verification_method: method, print: true, ...(selected.size > 0 ? { ticket_ids: [...selected] } : {}) }),
    onSuccess: (r) => {
      setNotice(`Handed over ${r.picked_up.length} ticket${r.picked_up.length === 1 ? '' : 's'}${r.print_job_ids.length ? ', printing' : ''}.`);
      setSelected(new Set());
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const reprint = useMutation({
    mutationFn: (ticketId: string) => endpoints.reprint(id, ticketId, 'Reprint at the window'),
    onSuccess: () => {
      setNotice('Reprinting; the earlier copy no longer admits.');
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  // Whether "refund as credit" is offered here: a switch in Admin (D-329); the credit lands on the customer's account (D-372).
  const features = useQuery({ queryKey: ['box-office-features'], queryFn: endpoints.features, staleTime: 30_000 });
  const refund = useMutation({
    mutationFn: async (asCredit: boolean) => {
      const itemIds = detail.data!.order.items.filter((i) => i.ticket && selected.has(i.ticket.id)).map((i) => i.id);
      const scope = itemIds.length === 0 ? 'FULL_ORDER' : 'TICKETS';
      const attempt = (pin?: string) => endpoints.refund(id, { scope, ...(scope === 'TICKETS' ? { order_item_ids: itemIds } : {}), reason_code: 'CUSTOMER_REQUEST', ...(pin ? { manager_pin: pin } : {}), ...(asCredit ? { as_credit: true } : {}) });
      try {
        return await attempt();
      } catch (cause) {
        if ((cause as ApiError).code !== 'MANAGER_APPROVAL_REQUIRED') throw cause;
        const pin = await askPin(`Refund ${scope === 'FULL_ORDER' ? 'the whole order' : `${itemIds.length} ticket(s)`} on ${detail.data!.order.order_number}`);
        if (!pin) throw new Error('Not approved');
        return attempt(pin);
      }
    },
    onSuccess: (r) => {
      // The code is what the customer leaves the window with, so it is said, not just recorded.
      if (r.refund.credit) setNotice(`${dollars(r.refund.credit.amount_cents)} is now account credit${r.refund.credit.email ? ` for ${r.refund.credit.email}` : ''} (balance ${dollars(r.refund.credit.balance_cents)}). It spends on tickets online or here${r.refund.credit.verified ? '' : ' once they sign in online with that email'}; there is no code to hand over.`);
      else setNotice(`Refund ${r.refund.refund_number} for ${dollars(r.refund.refund_total_cents)} posted.`);
      setSelected(new Set());
      refresh();
    },
    onError: (cause: Error) => setError(cause.message),
  });
  /**
   * Say the number before taking the money (D-277, D-278).
   *
   * The window asked "refund back to the way they paid?" and named no amount,
   * so the cashier found out what the refund came to at the same moment the
   * customer did — afterward. Worse, "the way they paid" is a guess on an
   * order settled with more than one thing.
   *
   * Quoted on the press rather than shown continuously: at a busy window an
   * always-live panel is noise, and this is the one moment it is read.
   */
  const [quoting, setQuoting] = useState(false);
  const quoteThenRefund = async (asCredit = false) => {
    setError(null);
    setQuoting(true);
    try {
      const itemIds = detail.data!.order.items.filter((i) => i.ticket && selected.has(i.ticket.id)).map((i) => i.id);
      const scope = itemIds.length === 0 ? 'FULL_ORDER' : 'TICKETS';
      const preview = await endpoints.previewRefund(id, { scope, ...(scope === 'TICKETS' ? { order_item_ids: itemIds } : {}), ...(asCredit ? { as_credit: true } : {}) });
      if (!preview.ok) {
        // Not an error: "everything refundable has already been refunded", or
        // "refund this at a register with an open drawer", is the answer.
        setError(preview.message);
        return;
      }
      const { estimate } = preview;
      const where = estimate.tenders.map((t) => `${dollars(t.amount_cents)} to ${t.label} — ${t.note}`).join('\n');
      const kept = estimate.kept.length > 0 ? `\n\nKept: ${estimate.kept.map((k) => `${k.name} ${dollars(k.amount_cents)}`).join(', ')}` : '';
      if (asCredit) {
        if (!window.confirm(`Give ${dollars(estimate.refund_total_cents)} back as credit?\n\nAccount credit for that amount on the customer's account, good for tickets. No money goes back to the card or the drawer.${kept}`)) return;
      } else if (!window.confirm(`Refund ${dollars(estimate.refund_total_cents)}?\n\n${where}${kept}`)) return;
      refund.mutate(asCredit);
    } catch (cause) {
      setError((cause as ApiError).message);
    } finally {
      setQuoting(false);
    }
  };

  const email = useMutation({
    mutationFn: () => {
      const to = window.prompt('Email the tickets to', detail.data?.order.customer.email ?? '');
      if (to === null) return Promise.reject(new Error('Canceled'));
      return endpoints.emailOrder(id, to.trim() || null);
    },
    onSuccess: () => setNotice('Email queued.'),
    onError: (cause: Error) => {
      if (cause.message !== 'Canceled') setError(cause.message);
    },
  });
  const receipt = useMutation({ mutationFn: () => endpoints.receipt(id), onSuccess: () => setNotice('Receipt printing.'), onError: (cause: ApiError) => setError(cause.message) });
  const payment = useQuery<OrderPaymentSummary, ApiError>({ queryKey: ['order', id, 'payment'], queryFn: () => endpoints.paymentSummary(id) });

  if (detail.error) return <ErrorNote>{detail.error.message}</ErrorNote>;
  if (!detail.data) return <p className="text-sm text-ink-400">Loading…</p>;
  const { order, tickets, pickups } = detail.data;
  const live = tickets.filter((t) => ['ISSUED', 'REISSUED', 'TRANSFERRED'].includes(t.status));
  // The shared rule, so a table seat says which table (D-337).
  const itemLabel = (item: (typeof order.items)[number]) => seatLabel({ section: item.section_name_snapshot, table: item.table_label_snapshot ?? null, row: item.row_label_snapshot, seat: item.seat_number_snapshot, ga_area: item.section_name_snapshot ? null : item.ga_area_name_snapshot });
  const startExchange = () => {
    const chosen = order.items.filter((i) => i.ticket && selected.has(i.ticket.id) && ['ISSUED', 'REISSUED'].includes(i.ticket.status));
    if (chosen.length === 0) return setError('Select the ticket(s) to exchange first; transferred tickets belong to their new holder');
    writeExchangeContext({ order_id: order.id, order_number: order.order_number, customer_name: order.customer.name, event_title: chosen[0]!.event_title_snapshot, tickets: chosen.map((i) => ({ id: i.ticket!.id, label: `${itemLabel(i)} · ${i.ticket_type_name_snapshot ?? ''}` })) });
    void navigate('/sell');
  };
  const toggle = (ticketId: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(ticketId)) next.delete(ticketId);
    else next.add(ticketId);
    return next;
  });
  return (
    <section className="mx-auto max-w-3xl space-y-3">
      <button type="button" onClick={onBack} className="touch rounded px-2 text-sm text-ink-600 hover:bg-page">
        ← Orders
      </button>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      <div className="rounded-xl border border-line bg-surface p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="font-mono text-xl font-semibold">{order.order_number}</h1>
          <span className="rounded bg-page px-2 py-0.5 text-xs font-semibold uppercase">{order.status.replaceAll('_', ' ')}</span>
        </div>
        <p className="text-sm text-ink-600">
          {order.customer.name ?? 'Guest'} {[order.customer.email, order.customer.phone].filter(Boolean).join(' · ')}
        </p>
        <p className="tabular mt-1 text-sm">
          Paid {dollars(order.totals.paid_cents)}
          {order.totals.refunded_cents > 0 ? ` · refunded ${dollars(order.totals.refunded_cents)}` : ''}
          {order.payments.map((p) => ` · ${p.tender_type.replaceAll('_', ' ').toLowerCase()}${p.card_last4 ? ` ····${p.card_last4}` : ''}${p.check_number ? ` #${p.check_number}` : ''} ${dollars(p.amount_cents)}`).join('')}
        </p>
        {pickups.length > 0 ? <p className="mt-1 text-xs text-ink-600">Picked up {when(pickups[0]!.picked_up_at)} by {pickups[0]!.staff_name} ({pickups[0]!.verification_method.replaceAll('_', ' ').toLowerCase()})</p> : null}
      </div>
      {payment.data && payment.data.balance.balance_due_cents > 0 ? (
        <BalancePanel
          summary={payment.data}
          order={order}
          onNotice={(message) => {
            setNotice(message);
            setError(null);
            void payment.refetch();
            refresh();
          }}
          onError={setError}
        />
      ) : null}
      <div className="rounded-xl border border-line bg-surface">
        <ul className="divide-y divide-line text-sm">
          {order.items
            .filter((i) => i.item_type === 'TICKET')
            .map((item) => {
              const ticket = tickets.find((t) => t.id === item.ticket?.id);
              const isLive = ticket ? live.some((l) => l.id === ticket.id) : false;
              return (
                <li key={item.id} className="flex items-center gap-3 px-5 py-3">
                  <input type="checkbox" disabled={!isLive} checked={ticket ? selected.has(ticket.id) : false} onChange={() => ticket && toggle(ticket.id)} aria-label="Select ticket" className="h-5 w-5" />
                  <span className="flex-1">
                    <span className="font-medium">
                      {item.event_title_snapshot} · {itemLabel(item)}
                    </span>
                    <span className="block text-xs text-ink-600">
                      {when(item.performance_starts_at_snapshot, item.timezone ?? undefined)} · {item.ticket_type_name_snapshot} · <span className="font-mono">{ticket?.short_code}</span> · v{ticket?.credential_version} · {ticket?.status.toLowerCase()}
                    </span>
                  </span>
                  <span className="tabular">{dollars(item.total_cents)}</span>
                  {isLive && can('box_office.reprint') ? (
                    <Button className="touch" disabled={reprint.isPending} onClick={() => reprint.mutate(ticket!.id)}>
                      Reprint
                    </Button>
                  ) : null}
                </li>
              );
            })}
        </ul>
      </div>
      {order.exchanges.length > 0 ? (
        <div className="rounded-xl border border-line bg-surface px-5 py-3 text-sm">
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Exchanges</p>
          <ul className="mt-1 divide-y divide-line">
            {order.exchanges.map((x) => (
              <li key={x.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                <span>
                  <span className="font-mono">{x.out.map((t) => t.short_code).join(', ')}</span> → <span className="font-mono">{x.in.map((t) => t.short_code).join(', ')}</span>
                  <span className="block text-xs text-ink-600">
                    {x.completed_at ? when(x.completed_at) : ''}
                    {x.staff_name ? ` · ${x.staff_name}` : ''}
                    {x.reason ? ` · ${x.reason}` : ''}
                  </span>
                </span>
                <span className="tabular text-xs text-ink-600">
                  {dollars(x.original_value_cents)} → {dollars(x.new_value_cents)}
                  {x.amount_due_cents > 0 ? ` · collected ${dollars(x.amount_due_cents)}` : ''}
                  {x.amount_refunded_cents > 0 ? ` · refunded ${dollars(x.amount_refunded_cents)}` : ''}
                  {x.adjustment_cents < 0 ? ` · waived ${dollars(-x.adjustment_cents)}` : x.adjustment_cents > 0 ? ` · forfeited ${dollars(x.adjustment_cents)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {can('box_office.will_call') && live.length > 0 ? (
          <Button variant="primary" className="touch" disabled={pickup.isPending} onClick={() => pickup.mutate('PHOTO_ID')}>
            Hand over {selected.size > 0 ? `${selected.size} selected` : 'all'} (ID checked)
          </Button>
        ) : null}
        {can('refunds.request') && live.length > 0 ? (
          <Button variant="danger" className="touch" disabled={refund.isPending || quoting} onClick={() => void quoteThenRefund()}>
            {quoting ? 'Working it out…' : `Refund ${selected.size > 0 ? 'selected' : 'order'}`}
          </Button>
        ) : null}
        {can('refunds.request') && live.length > 0 && features.data?.offer_credit_refunds ? (
          <Button className="touch" disabled={refund.isPending || quoting} onClick={() => void quoteThenRefund(true)}>
            {`Give ${selected.size > 0 ? 'selected' : 'order'} back as credit`}
          </Button>
        ) : null}
        {can('box_office.sell') && selected.size > 0 ? (
          <Button className="touch" onClick={startExchange}>
            Exchange selected
          </Button>
        ) : null}
        <Button className="touch" onClick={() => email.mutate()}>
          Email tickets
        </Button>
        <Button className="touch" onClick={() => receipt.mutate()}>
          Print receipt
        </Button>
        {live[0] ? (
          <a href={endpoints.ticketPdfUrl(order.id, live[0].id)} target="_blank" rel="noopener" className="touch inline-flex items-center rounded border border-line px-3 text-sm font-medium hover:bg-page">
            PDF
          </a>
        ) : null}
      </div>
    </section>
  );
}

/**
 * An order with money still owed (D-71): take a payment at the window with the
 * usual tenders, or email the customer a link to pay the rest themselves.
 */
function BalancePanel({ summary, order, onNotice, onError }: { summary: OrderPaymentSummary; order: OrderDetail['order']; onNotice: (message: string) => void; onError: (message: string) => void }) {
  const can = usePermissions();
  const askPin = useManagerPin();
  const [taking, setTaking] = useState(false);
  const due = summary.balance.balance_due_cents;
  const live = summary.requests.find((r) => r.status === 'ACTIVE' || r.status === 'PARTIALLY_PAID') ?? null;
  const cart: CartView = { cart_id: '', expires_at: null, customer: order.customer, performances: [], packages: [], totals: { face_value_cents: 0, discount_cents: 0, comp_cents: 0, fees_cents: 0, tax_cents: 0, customer_total_cents: due, tickets: 0 } };

  const sendLink = useMutation({
    mutationFn: async () => {
      const to = window.prompt('Email a payment link to', order.customer.email ?? '');
      if (to === null || !to.trim()) return Promise.reject(new Error('Canceled'));
      const attempt = (pin?: string) => endpoints.createPaymentRequest(order.id, { amount_policy: 'CUSTOM_AMOUNT_ALLOWED', recipient_email: to.trim(), recipient_name: order.customer.name, due_at: summary.payment_due_at, send: true, ...(pin ? { manager_pin: pin } : {}) });
      try {
        return await attempt();
      } catch (cause) {
        if ((cause as ApiError).code !== 'MANAGER_APPROVAL_REQUIRED') throw cause;
        const pin = await askPin(`Email a payment link for ${order.order_number}`);
        if (!pin) throw new Error('Not approved');
        return attempt(pin);
      }
    },
    onSuccess: (r) => onNotice(`Payment link emailed to ${r.payment_request.recipient_email_snapshot}.`),
    onError: (cause: Error) => {
      if (cause.message !== 'Canceled') onError(cause.message);
    },
  });
  const copyLink = useMutation({
    mutationFn: () => endpoints.copyPaymentRequest(live!.id),
    onSuccess: async (r) => {
      try {
        await navigator.clipboard.writeText(r.url);
        onNotice('Payment link copied.');
      } catch {
        window.prompt('Payment link', r.url);
      }
    },
    onError: (cause: ApiError) => onError(cause.message),
  });
  const resend = useMutation({ mutationFn: () => endpoints.resendPaymentRequest(live!.id, false), onSuccess: () => onNotice('Payment link emailed again.'), onError: (cause: ApiError) => onError(cause.message) });

  const take = async (tenders: Tender[]) => {
    const attempt = (pin?: string) => endpoints.collect(order.id, { tenders, receipt_email: true, ...(pin ? { manager_pin: pin } : {}) });
    let result;
    try {
      result = await attempt();
    } catch (cause) {
      if ((cause as ApiError).code !== 'MANAGER_APPROVAL_REQUIRED') throw cause;
      const pin = await askPin(`Take a payment on ${order.order_number}`);
      if (!pin) throw new Error('Not approved');
      result = await attempt(pin);
    }
    setTaking(false);
    onNotice(`Took ${dollars(result.payment.amount_cents)}${result.payment.change_cents ? `, change ${dollars(result.payment.change_cents)}` : ''}. ${result.payment.paid_in_full ? 'Paid in full; tickets follow the order’s issuance policy.' : `${dollars(result.payment.balance_after_cents)} still due.`}`);
  };

  return (
    <div className="rounded-xl border border-warn/40 bg-warn-soft p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Balance due</h2>
        <span className="tabular text-xl font-semibold">{dollars(due)}</span>
      </div>
      <p className="mt-1 text-sm text-ink-600">
        {dollars(summary.balance.paid_cents)} of {dollars(summary.balance.amount_due_cents)} paid
        {summary.payment_due_at ? ` · due ${summary.payment_due_at.slice(0, 10)}` : ''}
        {summary.past_due ? ` · ${summary.days_past_due} days past due` : ''}
        {summary.balance.in_flight_cents ? ` · ${dollars(summary.balance.in_flight_cents)} being paid online` : ''}
        {live ? ` · link ${live.status.toLowerCase()}${live.sent_at ? ` sent ${live.sent_at.slice(0, 10)}` : ''}${live.last_viewed_at ? ', opened' : ''}` : ' · no payment link'}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {can('orders.payments.collect') ? (
          <Button variant="primary" className="touch" onClick={() => setTaking(true)}>
            Take payment
          </Button>
        ) : null}
        {live ? (
          <>
            <Button className="touch" disabled={resend.isPending} onClick={() => resend.mutate()}>
              Resend link
            </Button>
            <Button className="touch" disabled={copyLink.isPending} onClick={() => copyLink.mutate()}>
              Copy link
            </Button>
          </>
        ) : (
          <Button className="touch" disabled={sendLink.isPending} onClick={() => sendLink.mutate()}>
            Email payment link
          </Button>
        )}
      </div>
      {taking ? <TenderPanel cart={cart} amount={due} title={`Payment on ${order.order_number}`} submitLabel="Take payment" showDelivery={false} onClose={() => setTaking(false)} onSubmit={(tenders) => take(tenders)} /> : null}
    </div>
  );
}
